import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { MenuItem } from 'primeng/api';
import { combineLatest, Subject, takeUntil } from 'rxjs';
import { ContactModel } from '../../../contact/models/contact.model';
import { ContactRepository } from '../../../contact/states/contact.repository';
import { AnimalPreventiveTreatmentModel } from '../../models/medical/animal-preventive-treatment.model';
import { DueTreatmentModel } from '../../models/medical/due-treatment.model';
import {
  DueTreatmentStatus,
  getDueTreatmentStatusLabelKey,
  getPreventiveTreatmentCategoryLabelKey,
  getPreventiveTreatmentTypeLabelKey,
  PreventiveTreatmentCategory,
  PreventiveTreatmentType
} from '../../models/medical/medical.enums';
import { formatVeterinarianName } from '../../models/medical/medical.helpers';
import { refId } from '../../models/medical/medical-ref.model';
import { AnimalMedicalRepository } from '../../states/medical/animal-medical.repository';
import { TreatmentGroup } from '../../states/medical/animal-medical.selectors';

type ProphylaxisStatus = DueTreatmentStatus | 'none';

interface ProphylaxisRow {
  key: string;
  category: PreventiveTreatmentCategory;
  type: PreventiveTreatmentType | null;
  preparation: string | null;
  batchNumber: string | null;
  administeredAt: string | null;
  dueAt: string | null;
  daysUntilDue: number | null;
  status: ProphylaxisStatus;
  recordId: number | null;
}

const STATUS_ORDER: Record<ProphylaxisStatus, number> = { overdue: 0, due: 1, upcoming: 2, none: 3 };

/** Lesesicht: Fälligkeiten je Kategorie und Erreger sowie der Medikationsverlauf. */
@Component({
  selector: 'app-medical-prophylaxis',
  templateUrl: './medical-prophylaxis.component.html',
  styleUrls: ['../medical-shared.scss'],
  standalone: false
})
export class MedicalProphylaxisComponent implements OnInit, OnDestroy {
  @Output() goToRecord = new EventEmitter<number>();

  protected readonly categoryLabelKey = getPreventiveTreatmentCategoryLabelKey;
  protected readonly typeLabelKey = getPreventiveTreatmentTypeLabelKey;

  rows: ProphylaxisRow[] = [];
  medications: AnimalPreventiveTreatmentModel[] = [];
  tiles = { overdue: 0, due: 0, upcoming: 0, none: 0 };
  statusFilter: ProphylaxisStatus | null = null;
  rowMenu: MenuItem[] = [];

  private _contactsById: Record<number, ContactModel> = {};
  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _medicalRepository: AnimalMedicalRepository,
    private readonly _contactRepository: ContactRepository,
    private readonly _translateService: TranslocoService
  ) {}

  ngOnInit(): void {
    combineLatest([
      this._medicalRepository.dueTreatments$,
      this._medicalRepository.latestTreatmentPerGroup$,
      this._medicalRepository.treatments$,
      this._contactRepository.selectById$(),
    ])
      .pipe(takeUntil(this._destroy$))
      .subscribe(([dueTreatments, groups, treatments, contactsById]) => {
        this._contactsById = contactsById;
        this.rows = this.buildRows(dueTreatments, groups, treatments);
        this.tiles = {
          overdue: this.rows.filter(row => row.status === DueTreatmentStatus.OVERDUE).length,
          due: this.rows.filter(row => row.status === DueTreatmentStatus.DUE).length,
          upcoming: this.rows.filter(row => row.status === DueTreatmentStatus.UPCOMING).length,
          none: this.rows.filter(row => row.status === 'none').length,
        };
        this.medications = treatments
          .filter(treatment => treatment.category === PreventiveTreatmentCategory.MEDICATION)
          .sort((a, b) => (b.administeredAt ?? '').localeCompare(a.administeredAt ?? '') || b.id - a.id);
      });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  get visibleRows(): ProphylaxisRow[] {
    return this.statusFilter ? this.rows.filter(row => row.status === this.statusFilter) : this.rows;
  }

  toggleStatusFilter(status: ProphylaxisStatus): void {
    this.statusFilter = this.statusFilter === status ? null : status;
  }

  statusLabelKey(status: ProphylaxisStatus): string {
    return status === 'none' ? 'animalMedical.prophylaxis.noFollowUp' : getDueTreatmentStatusLabelKey(status);
  }

  statusSeverity(status: ProphylaxisStatus): 'danger' | 'warn' | 'success' | 'secondary' {
    switch (status) {
      case DueTreatmentStatus.OVERDUE: return 'danger';
      case DueTreatmentStatus.DUE: return 'warn';
      case DueTreatmentStatus.UPCOMING: return 'success';
      default: return 'secondary';
    }
  }

  openRowMenu(row: ProphylaxisRow, menu: { toggle: (event: Event) => void }, event: Event): void {
    this.rowMenu = [{
      label: row.recordId
        ? this._translateService.translate('animalMedical.goToRecord', { id: row.recordId })
        : this._translateService.translate('animalMedical.noRecord'),
      icon: 'pi pi-book',
      disabled: !row.recordId,
      command: () => row.recordId && this.goToRecord.emit(row.recordId),
    }];
    menu.toggle(event);
  }

  veterinarianName(treatment: AnimalPreventiveTreatmentModel): string {
    const id = refId(treatment.veterinarian);
    return id !== null ? formatVeterinarianName(this._contactsById[id]) || `#${id}` : '–';
  }

  recordIdOf(treatment: AnimalPreventiveTreatmentModel): number | null {
    return refId(treatment.medicalRecord);
  }

  /**
   * Zeilen aus `due_treatments` plus die Gruppen ohne Folgetermin, die der Endpoint nicht liefert.
   * Eine Kombinationsimpfung erscheint mit einer Zeile je Erreger.
   */
  private buildRows(
    dueTreatments: DueTreatmentModel[],
    groups: TreatmentGroup[],
    treatments: AnimalPreventiveTreatmentModel[]
  ): ProphylaxisRow[] {
    const recordByTreatment = new Map(treatments.map(treatment => [treatment.id, refId(treatment.medicalRecord)]));

    const dueRows: ProphylaxisRow[] = dueTreatments.map(due => ({
      key: `${due.treatmentId}|${due.category}|${due.type ?? ''}`,
      category: due.category,
      type: due.type,
      preparation: due.preparation,
      batchNumber: due.batchNumber,
      administeredAt: due.administeredAt,
      dueAt: due.dueAt,
      daysUntilDue: due.daysUntilDue,
      status: due.status,
      recordId: recordByTreatment.get(due.treatmentId) ?? null,
    }));

    const noFollowUp: ProphylaxisRow[] = groups
      .filter(group => group.treatment.nextDueAt === null)
      .map(group => ({
        key: `${group.treatment.id}|${group.key}`,
        category: group.category,
        type: group.type,
        preparation: group.treatment.preparation,
        batchNumber: group.treatment.batchNumber,
        administeredAt: group.treatment.administeredAt,
        dueAt: null,
        daysUntilDue: null,
        status: 'none' as const,
        recordId: refId(group.treatment.medicalRecord),
      }));

    return [...dueRows, ...noFollowUp].sort((a, b) =>
      STATUS_ORDER[a.status] - STATUS_ORDER[b.status] || (a.dueAt ?? '').localeCompare(b.dueAt ?? '')
    );
  }
}
